import * as pc from "playcanvas";
import { getWeapon } from "@hupper/shared/weapons";
import type { GroundItemRenderer } from "../effects/GroundItemRenderer";

/**
 * "Press E to pick up <Weapon>" hint shown when the player's crosshair is
 * over a dropped item in range. Client-side raycast that matches the
 * server's PICKUP_RANGE / tolerance — the server still has final say
 * (when the player actually presses E, it re-raycasts authoritatively).
 *
 * Mirrors CS:GO's centered-on-crosshair affordance.
 */

// Source of truth lives in GroundItemSystem.ts; mirrored here for the
// hint raycast. Slightly more lenient on the perp tolerance so the prompt
// shows for items the player is "almost looking at" — feels better than
// only highlighting on a pixel-perfect crosshair hit.
const PICKUP_RANGE = 3.0;
const PICKUP_HALF_WIDTH = 0.65;

export class PickupPrompt {
  private readonly app: pc.AppBase;
  private readonly camera: pc.Entity;
  private readonly renderer: GroundItemRenderer;
  private readonly root: HTMLDivElement;
  private readonly label: HTMLSpanElement;
  private readonly unsubs: Array<() => void> = [];

  constructor(app: pc.AppBase, camera: pc.Entity, renderer: GroundItemRenderer) {
    this.app = app;
    this.camera = camera;
    this.renderer = renderer;

    // Build the DOM hint once and keep it hidden by default.
    const el = document.createElement("div");
    el.id = "pickup-prompt";
    el.style.cssText = [
      "position:fixed",
      // Sit just under the crosshair so it doesn't cover where the
      // player is aiming.
      "top:54%",
      "left:50%",
      "transform:translate(-50%,0)",
      "z-index:6",
      "display:none",
      "pointer-events:none",
      "padding:6px 12px",
      "border-radius:6px",
      "background:rgba(0,0,0,0.55)",
      "border:1px solid rgba(255,255,255,0.10)",
      "font-family:'Geist Mono',ui-monospace,monospace",
      "font-size:12px",
      "letter-spacing:0.04em",
      "color:#F5F5F0",
      "text-shadow:0 1px 0 rgba(0,0,0,0.6)",
    ].join(";");

    const key = document.createElement("span");
    key.textContent = "E";
    key.style.cssText = [
      "display:inline-block",
      "padding:1px 6px",
      "margin-right:8px",
      "border-radius:4px",
      "background:#FF6B00",
      "color:#fff",
      "font-weight:700",
      "letter-spacing:0",
    ].join(";");
    el.appendChild(key);

    const lbl = document.createElement("span");
    lbl.textContent = "Pick up";
    el.appendChild(lbl);

    document.body.appendChild(el);
    this.root = el;
    this.label = lbl;

    const onUpdate = () => this.tick();
    this.app.on("update", onUpdate);
    this.unsubs.push(() => this.app.off("update", onUpdate));
  }

  destroy(): void {
    for (const u of this.unsubs) u();
    this.root.remove();
  }

  private tick(): void {
    const eye = this.camera.getPosition();
    const fwd = this.camera.forward; // unit world forward
    let best: { id: number; weaponId: string; t: number } | null = null;
    for (const it of this.renderer.getItems()) {
      const dx = it.x - eye.x;
      const dy = it.y - eye.y;
      const dz = it.z - eye.z;
      const t = dx * fwd.x + dy * fwd.y + dz * fwd.z;
      if (t < 0 || t > PICKUP_RANGE) continue;
      const px = eye.x + fwd.x * t;
      const py = eye.y + fwd.y * t;
      const pz = eye.z + fwd.z * t;
      const perp = Math.hypot(it.x - px, it.y - py, it.z - pz);
      if (perp > PICKUP_HALF_WIDTH) continue;
      if (!best || t < best.t) best = { id: it.id, weaponId: it.weaponId, t };
    }
    if (best) {
      let name = best.weaponId;
      try { name = getWeapon(best.weaponId).name; } catch { /* keep raw id */ }
      // Only update the label text when it changes — avoids layout work
      // every frame.
      if (this.label.textContent !== `Pick up ${name}`) {
        this.label.textContent = `Pick up ${name}`;
      }
      if (this.root.style.display !== "inline-block") {
        this.root.style.display = "inline-block";
      }
    } else if (this.root.style.display !== "none") {
      this.root.style.display = "none";
    }
  }
}
